import { useEffect, useState, useRef } from "react";
import { createApplicationHubConnection } from "../infrastructure/signalr-service";
import { ApplicationListItem } from "../dtos/ApplicantListItem";
import { InterviewDetails } from "../dtos/ApplicationDetails";

export function useApplicationSignalR(
  organizationId: string | undefined,
  vacancyId: string | undefined,
  onNewApplication: (application: ApplicationListItem) => void,
  onApplicationUpdated?: (application: ApplicationListItem) => void,
  onInterviewUpdated?: (interview: InterviewDetails) => void
) {
  const [isConnected, setIsConnected] = useState(false);
  const onNewApplicationRef = useRef(onNewApplication);
  const onApplicationUpdatedRef = useRef(onApplicationUpdated);
  const onInterviewUpdatedRef = useRef(onInterviewUpdated);

  useEffect(() => {
    onNewApplicationRef.current = onNewApplication;
    onApplicationUpdatedRef.current = onApplicationUpdated;
    onInterviewUpdatedRef.current = onInterviewUpdated;
  }, [onNewApplication, onApplicationUpdated, onInterviewUpdated]);

  useEffect(() => {
    if (!organizationId || !vacancyId) {
      return;
    }

    const { connection, start, stop } = createApplicationHubConnection(
      organizationId
    );

    connection.on("NewApplication", (applicationDto: ApplicationListItem) => {
      if (applicationDto.vacancyId === vacancyId) {
        onNewApplicationRef.current(applicationDto);
      }
    });

    connection.on("ApplicationUpdated", (applicationDto: ApplicationListItem) => {
      if (applicationDto.vacancyId === vacancyId && onApplicationUpdatedRef.current) {
        onApplicationUpdatedRef.current(applicationDto);
      }
    });

    connection.on("InterviewUpdated", (interviewDto: InterviewDetails) => {
      if (onInterviewUpdatedRef.current) {
        onInterviewUpdatedRef.current(interviewDto);
      }
    });

    connection.onreconnecting(() => {
      console.log("SignalR Reconnecting...");
      setIsConnected(false);
    });

    connection.onreconnected(() => {
      console.log("SignalR Reconnected");
      setIsConnected(true);
    });

    connection.onclose(() => {
      console.log("SignalR Connection Closed");
      setIsConnected(false);
    });

    start().then(() => setIsConnected(true));

    return () => {
      stop();
    };
  }, [organizationId, vacancyId]);

  return { isConnected };
}
